import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useConfirmationDialogStore } from '@/state/confirmation-dialog.state';
import { useGlobalStore } from '@/state/global.state';
import { useWorkflowStore } from '@/state/workflow.state';
import { useNodeDuplication } from '@/hooks/useNodeDuplication';
import { FF_SHIFT_DELETE_DELETE_AND_KEEP_SELECTION } from '@/consts/featureFlags';
import { I18N_KEYS } from '@/language/keys';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { applyDraftToolOutputs } from '@/queries/useWorkflowApp';
import { useFlowView } from '../Recipe/FlowContext';
import { useToolTabAddNodeGuard, useToolTabEditGuard } from '../Recipe/hooks/useToolTabAddNodeGuard';
import {
  dropToolOutputs,
  placedToolOutputs,
  toolOutputRestoreUndo,
  toolOutputsRemovedByDelete,
  toolOutputsUnwiredByEdgeDelete,
} from './toolOutputDelete';
import type { TFunction } from 'i18next';
import type { DuplicateNodeOptions } from '@/components/Recipe/flowTypes';
import type { DeleteNodesOptions, DeleteNodesResult } from '@/components/Recipe/Views/FlowView';
import type { UndoRedoEntry } from '@/components/Recipe/undoRedo';
import type { EdgeId } from '@/types/node';
import type { NodeId } from 'web';
import type { DialogData } from '@/state/global.state';
import type { ImageRef } from '@/types/api/assets';
import type { ToolConfigOutput } from '@/types/tool-config.types';

/** The slice of the canvas a wire delete needs. Kept narrow so an edge component can call it. */
type EdgeDeleteFlow = {
  getEdges: () => readonly { id: string; target: NodeId }[];
  deleteEdges: (edgeIds: EdgeId[], options?: Pick<DeleteNodesOptions, 'alsoUndo'>) => void;
};

/**
 * Asks when this delete removes the last incoming wire of a saved tool output. Confirming drops
 * that output from the draft and deletes the wire in one undo step. Cancel leaves both.
 */
export function requestDeleteEdges(
  flowView: EdgeDeleteFlow,
  edgeIds: readonly EdgeId[],
  t: TFunction,
) {
  if (edgeIds.length === 0) {
    return;
  }
  const removed = toolOutputsUnwiredByEdgeDelete(
    edgeIds,
    flowView.getEdges(),
    useWorkflowStore.getState().recipe.toolConfig?.outputs ?? [],
  );
  const ids = [...edgeIds];
  if (removed.length === 0) {
    flowView.deleteEdges(ids);
    return;
  }
  useConfirmationDialogStore.getState().openConfirmationDialog({
    title: t(I18N_KEYS.RECIPE_MAIN.FLOW.APP_BUILDER_CANVAS.DISCONNECT_TOOL_OUTPUT_TITLE),
    message: t(I18N_KEYS.RECIPE_MAIN.FLOW.APP_BUILDER_CANVAS.DISCONNECT_TOOL_OUTPUT_MESSAGE),
    confirmBtnText: t(I18N_KEYS.RECIPE_MAIN.FLOW.APP_BUILDER_CANVAS.DISCONNECT_TOOL_OUTPUT_CONFIRM),
    cancelBtnText: t(I18N_KEYS.GENERAL.CANCEL),
    width: 'md',
    onConfirm: () => confirmEdgeDelete(flowView, ids, removed),
  });
}

async function confirmEdgeDelete(flowView: EdgeDeleteFlow, edgeIds: EdgeId[], removed: ToolConfigOutput[]) {
  const recipeId = useWorkflowStore.getState().recipe.id;
  const placed = placedToolOutputs(useWorkflowStore.getState().recipe.toolConfig?.outputs ?? [], removed);
  if (placed.length === 0) {
    flowView.deleteEdges(edgeIds);
    return;
  }
  const saved = await applyDraftToolOutputs(recipeId, (outputs) => dropToolOutputs(outputs, placed));
  if (!saved) {
    return;
  }
  const remaining = new Set(
    (useWorkflowStore.getState().recipe.toolConfig?.outputs ?? []).map((output) => output.nodeId),
  );
  // A refused write reports success and leaves the ids in place. Do not delete the wire then.
  if (placed.some((entry) => remaining.has(entry.output.nodeId))) {
    return;
  }
  flowView.deleteEdges(edgeIds, { alsoUndo: toolOutputRestoreUndo(recipeId, placed) });
}

/** Started during the user's click and settled once the delete is known to proceed or not. */
export type PreparedDelete = {
  commit: () => void;
  abort: () => void;
};

interface NodeActions {
  deleteNode: (nodeId: NodeId) => void;
  deleteNodes: (nodeIds: NodeId[], skipDialog?: boolean, onPrepared?: () => PreparedDelete | void) => void;
  deleteNodesPreservingBridgingConnections: (nodeIds: NodeId[], skipDialog?: boolean) => void;
  deleteSelected: () => void;
  duplicateNode: (nodeId: NodeId, options?: DuplicateNodeOptions) => void;
  duplicateNodes: (nodeIds: NodeId[], options?: DuplicateNodeOptions) => void;
  toggleLockNode: (nodeId: NodeId) => void;
  setNodesLockedState: (nodeIds: NodeId[], locked: boolean) => void;
  openOverlayDialog: (dialogData: DialogData) => void;
  closeOverlayDialog: () => void;
  setCoverImage: (poster: ImageRef) => Promise<void>;
}

export function useNodeActions(): NodeActions {
  const flowView = useFlowView();
  const isShiftDeleteBridgeEnabled = useFeatureFlagEnabled(FF_SHIFT_DELETE_DELETE_AND_KEEP_SELECTION);
  const { positionDuplicatedNodes, scrollNodeIntoViewIfNeeded } = useNodeDuplication();
  const { t } = useTranslation();
  const updateSnackbarData = useGlobalStore((s) => s.updateSnackbarData);
  const openOverlayDialog = useGlobalStore((s) => s.openDialog);
  const closeOverlayDialog = useGlobalStore((s) => s.closeDialog);
  const openConfirmationDialog = useConfirmationDialogStore((s) => s.openConfirmationDialog);
  const { getItem: getAllowDeleteGroupWithoutWarning } = useLocalStorage<boolean>('allowDeleteGroupWithoutWarning');

  const reportPreventedGroups = useCallback(
    (result: DeleteNodesResult) => {
      if (result.preventedGroups && result.preventedGroups.length > 0) {
        updateSnackbarData({
          text: t(I18N_KEYS.RECIPE_MAIN.FLOW.CANNOT_DELETE_GROUP_WITH_LOCKED_NODES),
          severity: 'default',
          icon: null,
          isOpen: true,
        });
      }
    },
    [t, updateSnackbarData],
  );

  const finishDelete = useCallback(
    (ids: NodeId[], preserveBridging: boolean, alsoUndo?: UndoRedoEntry) => {
      const result = flowView.deleteNodes(ids, {
        preserveBridgingConnections: preserveBridging,
        alsoUndo,
      });
      reportPreventedGroups(result);
    },
    [flowView, reportPreventedGroups],
  );

  const confirmOutputDelete = useCallback(
    async (
      ids: NodeId[],
      removed: ToolConfigOutput[],
      preserveBridging: boolean,
      onPrepared?: () => PreparedDelete | void,
    ) => {
      const recipeId = useWorkflowStore.getState().recipe.id;
      const placed = placedToolOutputs(useWorkflowStore.getState().recipe.toolConfig?.outputs ?? [], removed);
      // The minus button may already have cleared the output. The node delete still proceeds.
      if (placed.length === 0) {
        onPrepared?.()?.commit();
        finishDelete(ids, preserveBridging);
        return;
      }
      // A cut starts its clipboard write here, still inside the confirm click. The text is
      // filled in only if the draft update sticks. Aborting leaves the previous clipboard.
      const pending = onPrepared?.();
      const saved = await applyDraftToolOutputs(recipeId, (outputs) => dropToolOutputs(outputs, placed));
      if (!saved) {
        pending?.abort();
        return;
      }
      const remaining = new Set(
        (useWorkflowStore.getState().recipe.toolConfig?.outputs ?? []).map((output) => output.nodeId),
      );
      // A refused write reports success and leaves the ids in place. Do not delete the node then.
      if (placed.some((entry) => remaining.has(entry.output.nodeId))) {
        pending?.abort();
        return;
      }
      pending?.commit();
      finishDelete(ids, preserveBridging, toolOutputRestoreUndo(recipeId, placed));
    },
    [finishDelete],
  );

  // Every delete (keyboard, cut, node, group and selection menus) and lock toggle comes through
  // here, so the read-only Tool tab refuses them here once.
  const { guard: guardToolTabEdit } = useToolTabEditGuard();
  /**
   * A tool output asks before the node goes. Confirming removes the id from the draft and deletes
   * the node in one undo step. The group warning is skipped on that path so the user is not asked twice.
   */
  const deleteRequested = useCallback(
    (ids: NodeId[], skipDialog: boolean, preserveBridging: boolean, onPrepared?: () => PreparedDelete | void) => {
      if (ids.length > 0 && guardToolTabEdit()) return;
      const removed = toolOutputsRemovedByDelete(ids, useWorkflowStore.getState().recipe.toolConfig?.outputs ?? [], {
        isLocked: (id) => flowView.getEffectiveIsLocked(id),
        isGroup: (id) => flowView.isGroupNode(id),
        children: (id) =>
          flowView.getChildNodesOfGroup(id).map((child) => ({
            id: child.id,
            locked: child.data.isLocked === true,
          })),
      });
      if (removed.length > 0) {
        openConfirmationDialog({
          title: t(I18N_KEYS.RECIPE_MAIN.FLOW.APP_BUILDER_CANVAS.DELETE_TOOL_OUTPUT_TITLE),
          message: t(I18N_KEYS.RECIPE_MAIN.FLOW.APP_BUILDER_CANVAS.DELETE_TOOL_OUTPUT_MESSAGE),
          confirmBtnText: t(I18N_KEYS.RECIPE_MAIN.FLOW.APP_BUILDER_CANVAS.DELETE_TOOL_OUTPUT_CONFIRM),
          cancelBtnText: t(I18N_KEYS.GENERAL.CANCEL),
          // Design modal is the FPL medium preset (320px). Default `sm` is 240px.
          width: 'md',
          onConfirm: () => confirmOutputDelete(ids, removed, preserveBridging, onPrepared),
        });
        return;
      }

      if (!skipDialog && !getAllowDeleteGroupWithoutWarning()) {
        const groupIds = ids.filter((id) => flowView.isGroupNode(id));
        if (groupIds.length > 0) {
          openOverlayDialog({
            type: 'deleteGroup',
            allNodeIdsToDelete: ids,
            ...(preserveBridging ? { preserveBridgingConnections: true } : {}),
          });
          return;
        }
      }

      onPrepared?.()?.commit();
      finishDelete(ids, preserveBridging);
    },
    [
      confirmOutputDelete,
      finishDelete,
      flowView,
      getAllowDeleteGroupWithoutWarning,
      guardToolTabEdit,
      openConfirmationDialog,
      openOverlayDialog,
      t,
    ],
  );

  const deleteNodes = useCallback(
    (ids: NodeId[], skipDialog: boolean = false, onPrepared?: () => PreparedDelete | void) => {
      deleteRequested(ids, skipDialog, false, onPrepared);
    },
    [deleteRequested],
  );

  const deleteNodesPreservingBridgingConnections = useCallback(
    (ids: NodeId[], skipDialog: boolean = false) => {
      if (!isShiftDeleteBridgeEnabled) {
        deleteNodes(ids, skipDialog);
        return;
      }
      deleteRequested(ids, skipDialog, true);
    },
    [deleteNodes, deleteRequested, isShiftDeleteBridgeEnabled],
  );

  const deleteNode = useCallback((id: NodeId) => deleteNodes([id]), [deleteNodes]);

  const deleteSelected = useCallback(() => {
    if (flowView.selectedNodes.length > 0) {
      deleteNodes(flowView.selectedNodes);
    } else if (flowView.selectedEdges.length > 0) {
      if (guardToolTabEdit()) return;
      requestDeleteEdges(flowView, flowView.selectedEdges, t);
    }
  }, [flowView, deleteNodes, guardToolTabEdit, t]);

  // Every Duplicate (keyboard, node menu, group and selection menus) comes through here, so the
  // Tool tab, which adds no nodes, refuses it here once.
  const { guard: guardToolTabAddNode } = useToolTabAddNodeGuard();
  const duplicateNode = useCallback(
    (id: NodeId, options?: DuplicateNodeOptions) => {
      if (guardToolTabAddNode()) return;
      flowView.duplicateNodes([id], positionDuplicatedNodes, scrollNodeIntoViewIfNeeded, options);
    },
    [flowView, guardToolTabAddNode, positionDuplicatedNodes, scrollNodeIntoViewIfNeeded],
  );
  const duplicateNodes = useCallback(
    (nodeIds: NodeId[], options?: DuplicateNodeOptions) => {
      if (nodeIds.length > 0 && guardToolTabAddNode()) return;
      flowView.duplicateNodes(nodeIds, positionDuplicatedNodes, scrollNodeIntoViewIfNeeded, options);
    },
    [flowView, guardToolTabAddNode, positionDuplicatedNodes, scrollNodeIntoViewIfNeeded],
  );
  const toggleLockNode = useCallback(
    (id: NodeId) => {
      if (guardToolTabEdit()) return;
      flowView.toggleLockNode(id);
    },
    [flowView, guardToolTabEdit],
  );
  const setNodesLockedState = useCallback(
    (nodeIds: NodeId[], locked: boolean) => {
      if (guardToolTabEdit()) return;
      flowView.setNodesLockedState(nodeIds, locked);
    },
    [flowView, guardToolTabEdit],
  );
  const setCoverImage = useWorkflowStore((s) => s.setRecipePoster);

  return {
    deleteNode,
    deleteNodes,
    deleteNodesPreservingBridgingConnections,
    deleteSelected,
    duplicateNode,
    duplicateNodes,
    toggleLockNode,
    setNodesLockedState,
    openOverlayDialog,
    closeOverlayDialog,
    setCoverImage,
  };
}
